/**
 * The password rule (spec.md FR-045, owner decision 2026-09-24): at least 16 characters, of which
 * at least 4 digits, 4 letters and 4 special characters.
 *
 * The sign-in service (Cognito) can only require "at least one" digit or symbol, so the counts are
 * checked here, on every screen where a person chooses a password. Special characters are exactly
 * Cognito's symbol set, plus an inner space, so a password passing here is never refused there.
 */

export const PASSWORD_RULES = {
  minLength: 16,
  minDigits: 4,
  minLetters: 4,
  minSpecial: 4,
} as const;

export type PasswordProblem =
  'tooShort' | 'notEnoughDigits' | 'notEnoughLetters' | 'notEnoughSpecial';

/** Cognito's password symbols: ^ $ * . [ ] { } ( ) ? " ! @ # % & / \ , > < ' : ; | _ ~ ` = + - */
const SPECIAL = new Set('^$*.[]{}()?"!@#%&/\\,><\':;|_~`=+-');

/** The unmet rules, in a fixed order; empty when the password is acceptable. */
export function passwordProblems(password: string): PasswordProblem[] {
  const chars = [...password.normalize('NFC')];
  const count = (test: (c: string, i: number) => boolean) => chars.filter(test).length;

  const digits = count((c) => /[0-9]/.test(c));
  const letters = count((c) => /\p{L}/u.test(c));
  const special = count((c, i) => SPECIAL.has(c) || (c === ' ' && i > 0 && i < chars.length - 1));

  const problems: PasswordProblem[] = [];
  if (chars.length < PASSWORD_RULES.minLength) problems.push('tooShort');
  if (digits < PASSWORD_RULES.minDigits) problems.push('notEnoughDigits');
  if (letters < PASSWORD_RULES.minLetters) problems.push('notEnoughLetters');
  if (special < PASSWORD_RULES.minSpecial) problems.push('notEnoughSpecial');
  return problems;
}
